import { useLang } from "@/lib/i18n";
import { SITE } from "@/data/site";

const COPY = {
  en: {
    eyebrow: "Terms of Service",
    title: "Terms of Service",
    company: "Faster Plumbing",
    lastUpdated: "Last Updated: June 14, 2026",
    intro:
      "These Terms of Service govern your use of Faster Plumbing's services, website, and SMS messaging program. By contacting us, submitting a service request, or opting in to receive text messages, you agree to these terms.",
    sections: [
      {
        h: "1. SMS Program Description",
        p: "Faster Plumbing operates an A2P 10DLC SMS messaging program to communicate with customers across South Florida, including Palm Beach, Broward, Miami-Dade, and Martin Counties. By opting in, you agree to receive recurring automated text messages for the following purposes:",
        items: [
          "Appointment scheduling, confirmations, and reminders",
          "Technician dispatch and arrival notifications",
          "Service estimates, updates, and job completion notices",
          "Invoices and payment-related notifications",
          "Promotional offers, discounts, and seasonal specials",
          "Review and customer satisfaction follow-ups",
          "General customer support communications",
        ],
        tail: "Message frequency varies based on your activity and service schedule.",
      },
      {
        h: "2. How to Opt Out",
        p: "You may opt out of SMS messages at any time by replying STOP to any text message from us. You will receive one final confirmation and no further messages will be sent. To re-subscribe, text START to our business number or opt in again via our website.",
      },
      {
        h: "3. Support & Contact Information",
        contactBlock: true,
        p: "For help with our SMS program, reply HELP to any message or contact us directly:",
      },
      {
        h: "4. Message & Data Rates",
        p: "Standard message and data rates may apply to all SMS messages sent and received. These charges are billed by your mobile carrier. Faster Plumbing does not charge for SMS messages. Contact your wireless provider for details about your plan and applicable rates.",
      },
      {
        h: "5. Carrier Liability Disclaimer",
        p: "Mobile carriers (such as AT&T, Verizon, T-Mobile, and others) are not liable for delayed or undelivered messages. Message delivery is subject to your network operator's availability and coverage. Faster Plumbing is not responsible for messages that are delayed, blocked, or undelivered due to factors outside our control, including carrier filtering, network outages, or device settings.",
      },
      {
        h: "6. Age Restriction",
        p: "Our SMS program and services are intended for individuals 18 years of age or older. By opting in or submitting a service request, you confirm that you are at least 18 years old. We do not knowingly collect information from or send marketing messages to anyone under the age of 18.",
      },
      {
        h: "7. Privacy Policy",
        privacyLinked: true,
      },
      {
        highlight:
          "SMS opt-in data and consent records are never sold, rented, or shared with any third party or affiliate. Faster Plumbing uses this information solely to communicate with you directly.",
      },
      {
        h: "8. Services Covered",
        p: "Faster Plumbing provides licensed plumbing services including, but not limited to:",
        items: [
          "Water Heater Repair & Installation",
          "Certified Backflow Installation, Inspection & Repair",
          "Sewer Repair & Replacement",
          "Lavatory, Shower & Kitchen Faucet Services",
          "Septic & Drain Fields",
          "Water Shut-Off Valve Service",
          "Toilet Repair & Installation",
          "Drain Cleaning & Clog Removal",
        ],
      },
      {
        h: "9. Modifications",
        p: "Faster Plumbing reserves the right to modify these Terms of Service at any time. Changes will be posted on this page with an updated effective date. Continued use of our services or SMS program after any modification constitutes your acceptance of the updated Terms.",
      },
    ],
    privacyLinkedPre: "Your use of our SMS program and services is also governed by our ",
    privacyLinkedLink: "Privacy Policy",
    privacyLinkedPost:
      ", which explains how we collect, use, and protect your personal data. Our Privacy Policy confirms that your SMS opt-in information will never be sold or shared with third parties for marketing purposes.",
    coverageLine: "Lake Worth Beach, FL — Serving Palm Beach, Broward, Miami-Dade & Martin Counties",
    phoneLabel: "Phone",
    emailLabel: "Email",
    websiteLabel: "Website",
    rights: "All rights reserved.",
    relatedPrivacy: "Privacy Policy",
  },
  es: {
    eyebrow: "Términos del Servicio",
    title: "Términos del Servicio",
    company: "Faster Plumbing",
    lastUpdated: "Última actualización: 14 de junio de 2026",
    intro:
      "Estos Términos del Servicio rigen el uso de los servicios, sitio web y programa de mensajería SMS de Faster Plumbing. Al contactarnos, enviar una solicitud de servicio o suscribirse para recibir mensajes de texto, usted acepta estos términos.",
    sections: [
      {
        h: "1. Descripción del Programa SMS",
        p: "Faster Plumbing opera un programa de mensajería SMS A2P 10DLC para comunicarse con clientes en el Sur de Florida, incluyendo los condados de Palm Beach, Broward, Miami-Dade y Martin. Al suscribirse, usted acepta recibir mensajes de texto automatizados recurrentes para los siguientes fines:",
        items: [
          "Programación, confirmación y recordatorios de citas",
          "Notificaciones de despacho y llegada de técnicos",
          "Cotizaciones, actualizaciones y avisos de finalización de trabajos",
          "Facturas y notificaciones relacionadas con pagos",
          "Ofertas promocionales, descuentos y especiales de temporada",
          "Solicitudes de reseñas y seguimiento de satisfacción del cliente",
          "Comunicaciones generales de atención al cliente",
        ],
        tail: "La frecuencia de los mensajes varía según su actividad y agenda de servicio.",
      },
      {
        h: "2. Cómo Cancelar la Suscripción",
        p: "Puede cancelar los mensajes SMS en cualquier momento respondiendo STOP a cualquier mensaje de texto nuestro. Recibirá una confirmación final y no se enviarán más mensajes. Para volver a suscribirse, envíe START a nuestro número comercial o suscríbase nuevamente a través de nuestro sitio web.",
      },
      {
        h: "3. Soporte e Información de Contacto",
        contactBlock: true,
        p: "Para obtener ayuda con nuestro programa SMS, responda HELP a cualquier mensaje o contáctenos directamente:",
      },
      {
        h: "4. Tarifas de Mensajes y Datos",
        p: "Pueden aplicarse las tarifas estándar de mensajes y datos a todos los mensajes SMS enviados y recibidos. Estos cargos son facturados por su proveedor móvil. Faster Plumbing no cobra por los mensajes SMS. Contacte a su proveedor inalámbrico para obtener detalles sobre su plan y las tarifas aplicables.",
      },
      {
        h: "5. Descargo de Responsabilidad del Operador",
        p: "Los operadores móviles (como AT&T, Verizon, T-Mobile y otros) no son responsables por mensajes retrasados o no entregados. La entrega de mensajes está sujeta a la disponibilidad y cobertura de su operador de red. Faster Plumbing no es responsable por mensajes retrasados, bloqueados o no entregados debido a factores fuera de nuestro control, incluido el filtrado del operador, interrupciones de la red o la configuración del dispositivo.",
      },
      {
        h: "6. Restricción de Edad",
        p: "Nuestro programa SMS y servicios están destinados a personas de 18 años o más. Al suscribirse o enviar una solicitud de servicio, usted confirma que tiene al menos 18 años. No recopilamos información ni enviamos mensajes de marketing a menores de 18 años a sabiendas.",
      },
      {
        h: "7. Política de Privacidad",
        privacyLinked: true,
      },
      {
        highlight:
          "Los datos de suscripción SMS y los registros de consentimiento nunca se venden, alquilan ni se comparten con ningún tercero o afiliado. Faster Plumbing utiliza esta información únicamente para comunicarse con usted directamente.",
      },
      {
        h: "8. Servicios Cubiertos",
        p: "Faster Plumbing ofrece servicios de plomería con licencia, incluyendo, entre otros:",
        items: [
          "Reparación e instalación de calentadores de agua",
          "Instalación, inspección y reparación certificada de backflow",
          "Reparación y reemplazo de cloaca",
          "Servicios de grifería de baño, ducha y cocina",
          "Tanques sépticos y campos de drenaje",
          "Servicio de válvula de cierre de agua",
          "Reparación e instalación de inodoros",
          "Limpieza de drenajes y eliminación de atascos",
        ],
      },
      {
        h: "9. Modificaciones",
        p: "Faster Plumbing se reserva el derecho de modificar estos Términos del Servicio en cualquier momento. Los cambios se publicarán en esta página con una fecha de vigencia actualizada. El uso continuado de nuestros servicios o programa SMS después de cualquier modificación constituye su aceptación de los Términos actualizados.",
      },
    ],
    privacyLinkedPre: "El uso de nuestro programa SMS y servicios también se rige por nuestra ",
    privacyLinkedLink: "Política de Privacidad",
    privacyLinkedPost:
      ", que explica cómo recopilamos, usamos y protegemos sus datos personales. Nuestra Política de Privacidad confirma que su información de suscripción SMS nunca será vendida ni compartida con terceros para fines de marketing.",
    coverageLine: "Lake Worth Beach, FL — Servimos Palm Beach, Broward, Miami-Dade y Martin",
    phoneLabel: "Teléfono",
    emailLabel: "Correo",
    websiteLabel: "Sitio web",
    rights: "Todos los derechos reservados.",
    relatedPrivacy: "Política de Privacidad",
  },
};

export default function Terms() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;

  return (
    <>
      <section className="bg-brand-navy-dark text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-navy-dark via-brand-navy to-slate-900" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,191,0,0.15)_0%,transparent_55%)]" />
        </div>
        <div className="absolute -top-32 -right-24 w-[440px] h-[440px] rounded-full bg-brand-yellow/15 blur-3xl pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/70 to-transparent" />
        <div className="relative container-max py-16 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-yellow text-slate-900 px-3.5 py-1.5 text-xs font-bold tracking-wide shadow-lg shadow-yellow-300/20">
            {c.eyebrow}
          </div>
          <h1
            className="mt-5 font-heading text-4xl sm:text-5xl font-bold leading-tight"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.45)" }}
          >
            {c.title}
          </h1>
          <p className="mt-3 text-white/85 text-lg">
            <strong>{c.company}</strong> — {c.lastUpdated}
          </p>
        </div>
      </section>

      <section className="section-padding bg-slate-50">
        <div className="container-max max-w-3xl">
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-7 sm:p-10">
            <p className="text-slate-700 leading-relaxed">{c.intro}</p>

            <div className="mt-8 space-y-7">
              {c.sections.map((s, i) => {
                if (s.highlight) {
                  return (
                    <div
                      key={`hl-${i}`}
                      className="bg-brand-yellow/15 border-2 border-brand-yellow rounded-2xl px-5 py-4"
                    >
                      <p className="text-sm font-semibold text-slate-900 leading-relaxed flex items-start gap-2.5">
                        <span aria-hidden="true">🔒</span>
                        <span>{s.highlight}</span>
                      </p>
                    </div>
                  );
                }
                return (
                  <div key={s.h}>
                    <h2 className="font-heading text-base font-bold text-slate-900 flex items-start gap-3">
                      <span className="mt-2 w-2 h-2 rounded-full bg-brand-yellow shrink-0" />
                      {s.h}
                    </h2>
                    {s.privacyLinked ? (
                      <p className="mt-2 text-slate-700 leading-relaxed pl-5">
                        {c.privacyLinkedPre}
                        <a
                          href="/privacy"
                          className="font-bold text-brand-yellow-dark hover:underline"
                        >
                          {c.privacyLinkedLink}
                        </a>
                        {c.privacyLinkedPost}
                      </p>
                    ) : (
                      <>
                        {s.p && (
                          <p className="mt-2 text-slate-700 leading-relaxed pl-5">{s.p}</p>
                        )}
                        {s.items && (
                          <ul className="mt-2 pl-5 space-y-1.5">
                            {s.items.map((it) => (
                              <li
                                key={it}
                                className="text-slate-700 leading-relaxed flex items-start gap-2.5"
                              >
                                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                                <span>{it}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {s.tail && (
                          <p className="mt-3 text-slate-700 leading-relaxed pl-5">{s.tail}</p>
                        )}
                        {s.contactBlock && (
                          <div className="mt-3 ml-5 bg-slate-50 border border-slate-200 rounded-2xl p-5">
                            <p className="text-sm text-slate-700 leading-relaxed">
                              <strong className="text-slate-900">{c.company}</strong>
                              <br />
                              {c.coverageLine}
                              <br />
                              {c.phoneLabel}:{" "}
                              <a
                                href={`tel:${SITE.primaryPhone}`}
                                className="font-bold text-brand-yellow-dark hover:underline"
                              >
                                {SITE.primaryPhone}
                              </a>
                              <br />
                              {c.emailLabel}:{" "}
                              <a
                                href={`mailto:${SITE.email}`}
                                className="font-bold text-brand-yellow-dark hover:underline"
                              >
                                {SITE.email}
                              </a>
                              <br />
                              {c.websiteLabel}:{" "}
                              <a
                                href="https://fasterplumbing.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-bold text-brand-yellow-dark hover:underline"
                              >
                                fasterplumbing.com
                              </a>
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            <p className="mt-10 text-xs text-slate-400 text-center">
              © {c.company}. {c.rights} ·{" "}
              <a href="/privacy" className="text-slate-400 hover:text-brand-yellow-dark">
                {c.relatedPrivacy}
              </a>
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
